import createClient from "openapi-fetch";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { paths as AdminPaths } from "@dolfin/api-admin";
import { useAuth } from "@/auth/AuthContext";
import { useDolfinAuth, useDolfinClient } from "@/components/DolfinProvider";
import { DemoContext, type DemoState } from "./DemoContext";

/** The /v1/demo routes are hidden from v1, so their types come from the admin document (as Books' do). */
export function useDemoApi() {
  const { baseUrl, authFetch } = useDolfinAuth();
  return useMemo(() => {
    // Body read as text: re-wrapping a Request's stream body needs `duplex`, and every body here is JSON or none.
    const fetch = async (r: Request) =>
      authFetch(r.url, {
        method: r.method,
        headers: r.headers,
        body: r.method === "GET" || r.method === "HEAD" ? undefined : await r.text(),
        signal: r.signal,
      });
    return createClient<AdminPaths>({ baseUrl, fetch });
  }, [baseUrl, authFetch]);
}

/** Says whether the active organisation is a demo, and reads its seeding state and expiry for the banner. */
export function DemoProvider({ children }: { children: ReactNode }) {
  const api = useDolfinClient();
  const { activeOrganisationId: organisationId, getClientType } = useAuth();
  const clientType = getClientType(organisationId);
  // Writes open only for a known Live or Sandbox org: an unknown type (an older session, /me still loading) stays
  // read-only rather than briefly letting a demo write.
  const isDemo = !!organisationId && clientType !== "Live" && clientType !== "Sandbox";
  const [demo, setDemo] = useState<DemoState | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => setDemo(null), [organisationId]);

  useEffect(() => {
    if (!organisationId || !isDemo) return;
    let cancelled = false;
    // A refresh keeps the state it had until the read lands; a failed read keeps it too (the gates don't depend on it).
    void api
      .GET("/v1/organisations/{id}", { params: { path: { id: organisationId } } })
      .then(({ data }) => {
        if (cancelled || !data) return;
        setDemo(data.isDemo ? { status: data.demoStatus ?? "NotStarted", expiresAt: data.demoExpiresAt ?? null } : null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api, organisationId, isDemo, tick]);

  const refresh = useCallback(() => setTick((n) => n + 1), []);
  const value = useMemo(() => ({ demo, isDemo, refresh }), [demo, isDemo, refresh]);
  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}
